import { Box, Grid, IconButton, Skeleton, Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { makeStyles } from "tss-react/mui"
import { Button } from "../../../../../office/admin/components/Button"
import { fetchAvailableResource, ResourceAvailabilityResponse, UserWeekMenu } from "../../weekMenuApi"
import { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleXmark } from "@fortawesome/pro-light-svg-icons"
import { faLightbulb } from "@fortawesome/pro-solid-svg-icons"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ListComponent, RoomListItemSkeleton } from "./RoomListItem"
import { ResourceFilterMenu } from "../../../../../../shared/components/createOrder/components/resource/resourceFilters/ResourceFilterMenu"
import { ResourceFiltersTypes } from "../../../../../../shared/components/createOrder/createOrderTypes"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { CreateUpdateBookingDialogWrap } from "../../../../../../shared/components/createOrder/components/resource/CreateUpdateBookingDialogWrap"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { OrderType } from "../../../../../payments/orderTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { CreateUpdateBookingFormValues } from "../../../../../../shared/components/dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import {
    selectEffectiveCompanyId,
    selectEffectiveUserId,
    selectEffectiveUsername,
    selectEffectiveUserType,
} from "../../../../../context/contextSelectors"
import { CreateOrderType } from "../../../../../../shared/components/createOrder/helpers"
import {
    BookingConfigurationResponse,
    ConfigQuery,
    createBooking,
    fetchBookingConfiguration,
} from "../../../../../../shared/components/createOrder/createOrderApi"
import { DateTime } from "luxon"
import { OrganizerType } from "../../../../../../shared/types/OrganizerTypes"
import { useOrderDialogUrlState } from "../../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"

const useStyles = makeStyles()(() => ({
    title: {
        fontSize: "1.75rem",
        fontWeight: 700,
    },
    pageHeaderArea: {
        paddingBottom: "2rem",
    },
    inputDropdown: {
        width: "11rem",
    },
    container: {
        height: "100vh",
        display: "flex",
        flexDirection: "column",
    },
    list: {
        flex: 1,
        width: "100%",
        overflowY: "auto",
        padding: "1rem 0",
    },
    footer: {
        width: "100%",
        position: "sticky",
        bottom: 0,
        zIndex: 1,
        textAlign: "center",
    },
    iconContainer: {
        backgroundColor: tailwindPalette.blue[100],
        color: tailwindPalette.blue[500],
        borderRadius: "0.5rem",
        width: "2.75rem",
        height: "2.75rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    textButton: {
        color: tailwindPalette.blue[600],
        padding: "0 !important",
        textDecoration: "underline",
    },
}))

const selectBookingConfig = (data: BookingConfigurationResponse) => data?.bookingConfig

const transformResource = (data: ResourceAvailabilityResponse) => {
    return data.resourceTypes?.[0]?.resources ?? []
}

export function RoomFinder({
    onClose,
    openSuccessDialog,
}: {
    onClose: () => void
    openSuccessDialog: (orderId: number) => void
}) {
    const { classes } = useStyles()
    const [confirmBookingDialog, setConfirmBookingDialog] = useState<boolean>(false)
    const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null)
    const [selectedEndTime, setSelectedEndTime] = useState<string | null>(null)

    const userType = useAppSelector(selectEffectiveUserType)!
    const userId = useAppSelector(selectEffectiveUserId) || 0
    const companyId = useAppSelector(selectEffectiveCompanyId) || 0
    const username = useAppSelector(selectEffectiveUsername)
    const { openDialog } = useOrderDialogUrlState()

    const [selectedIds, setSelectedIds] = useState<ReadonlyArray<number>>([])
    const [filters, setFilters] = useState<ResourceFiltersTypes>({
        filters: [
            {
                identifier: "sittings",
                isRemovable: false,
                values: [
                    {
                        key: 2,
                        value: 2,
                    },
                ],
            },
            {
                identifier: "startDateTime",
                isRemovable: false,
                values: [
                    {
                        key: DateTime.now().toString(),
                        value: DateTime.now().toString(),
                    },
                ],
            },
            {
                identifier: "meetingDuration",
                isRemovable: false,
                values: [
                    {
                        key: 60,
                        value: 60,
                    },
                ],
            },
        ],
        search: "",
    })

    const { data: bookingConfig } = useApiQuery(fetchBookingConfiguration, {
        queryName: ConfigQuery.BOOKING_CONFIG,
        dependencies: { userId },
        select: selectBookingConfig,
        enabled: !!selectedIds.length,
    })

    function handleSelect(id: number) {
        setSelectedIds((prevSelectedIds) =>
            prevSelectedIds.includes(id)
                ? prevSelectedIds.filter((selectedId) => selectedId !== id)
                : [...prevSelectedIds, id],
        )
    }

    function handleTimeSelection(startTime: string | null, endTime: string | null) {
        setSelectedStartTime(startTime)
        setSelectedEndTime(endTime)
    }

    const { data, isLoading } = useApiQuery(fetchAvailableResource, {
        queryName: UserWeekMenu.AVAILABLE_RESOURCE,
        dependencies: {
            sittings: filters.filters.find((f) => f.identifier === "sittings")?.values?.[0]?.value ?? 0,
            resourceTypeIds:
                filters.filters.find((f) => f.identifier === "resourceTypeIds")?.values.map((v) => v.value) ?? [],
            meetingDuration: filters.filters.find((f) => f.identifier === "meetingDuration")?.values[0]?.value ?? 0,
            startDateTime: filters.filters.find((f) => f.identifier === "startDateTime")?.values[0]?.value,
            meetingType: filters.filters.find((f) => f.identifier === "meetingType")?.values[0]?.value,
            serviceCenterIds:
                filters.filters.find((f) => f.identifier === "serviceCenterIds")?.values.map((v) => v.value) ?? [],
            floors: filters.filters.find((f) => f.identifier === "floors")?.values.map((v) => v.value) ?? [],
            tableSetupIds:
                filters.filters.find((f) => f.identifier === "tableSetupIds")?.values.map((v) => v.value) ?? [],
            fixedAvIds: filters.filters.find((f) => f.identifier === "fixedAvIds")?.values.map((v) => v.value) ?? [],
        },
        select: transformResource,
    })

    const { mutateAsync: callCreateBooking } = useApiMutation(createBooking, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [UserWeekMenu.MEETING_SERVICE_LIST] })
            setConfirmBookingDialog(false)
            onClose()
            openSuccessDialog(data.order.id)
        },
    })

    const onCreateBooking = async (values: CreateUpdateBookingFormValues) => {
        await callCreateBooking({
            userId,
            content: {
                orderType: OrderType.MEETING,
                organizers: [
                    {
                        id: userId,
                        organizerType: OrganizerType.CREATOR,
                    },
                ],
                bookingDetails: {
                    resources: values.meetingDetails.resourceIds.map((i) => ({
                        id: +i,
                        tableSetups: values.resources.find((r) => r.id === +i)?.tableSetups,
                    })),
                },
                messageToServiceCenter: values.messageToServiceCenter,
                meetingDetails: {
                    attendees: values.attendees,
                    meetingName: values.meetingDetails.meetingName,
                    isPrivate: values.meetingDetails.isPrivate,
                    isExternal: values.meetingDetails.isExternal,
                    startDateTime: selectedStartTime ?? "",
                    endDateTime: selectedEndTime ?? "",
                },
            },
        })
    }

    const handelCreateOrder = () => {
        openDialog({
            activeOrderTab: CreateOrderType.RESOURCE_BOOKING,
        })
        onClose()
    }

    return (
        <Box className={classes.container}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className={classes.pageHeaderArea}
            >
                <Stack direction="row" alignItems="center" spacing=".75rem">
                    <Box className={classes.iconContainer}>
                        <FontAwesomeIcon size="lg" icon={faLightbulb} />
                    </Box>
                    <LocalizedStrict id="user-homeroom-finder-title">
                        <Typography display="inline" className={classes.title}>
                            Room finder
                        </Typography>
                    </LocalizedStrict>
                </Stack>
                <Stack direction="row" alignItems="center" spacing=".75rem">
                    <LocalizedStrict id="user-home-room-finder-go-to-timeline-button">
                        <StandardButton onClick={handelCreateOrder} className={classes.textButton} variant="text">
                            Go To Timeline
                        </StandardButton>
                    </LocalizedStrict>
                    <IconButton onClick={onClose}>
                        <FontAwesomeIcon icon={faCircleXmark} size="sm" />
                    </IconButton>
                </Stack>
            </Stack>
            <Grid container gap="1rem" paddingBottom="1.5rem" alignItems="center" flexWrap="nowrap">
                <ResourceFilterMenu filters={filters.filters} userId={userId} setFilters={setFilters} />
            </Grid>
            <Grid container gap="2rem" columns={{ xs: 12, md: 12 }}>
                <Stack width="100%" direction="column" spacing="1rem">
                    <Stack direction="column" alignItems="end">
                        {data?.length ? (
                            <LocalizedStrict
                                id="user-home-room-finder-resource-results-amount"
                                vars={{ amount: data?.length }}
                            >
                                <Typography color="textSecondary">{data?.length} results</Typography>
                            </LocalizedStrict>
                        ) : (
                            <Skeleton width="3rem" height="2rem" />
                        )}
                    </Stack>
                    <Box className={classes.list}>
                        {isLoading ? (
                            <Stack spacing="1rem">
                                {Array.from({ length: 5 }, () => (
                                    <RoomListItemSkeleton />
                                ))}
                            </Stack>
                        ) : (
                            <ListComponent
                                availableResources={data}
                                onSelect={handleSelect}
                                onTimeSelection={handleTimeSelection}
                                selectedIds={selectedIds}
                            />
                        )}
                    </Box>
                    {!isLoading && selectedIds.length ? (
                        <Box className={classes.footer}>
                            <LocalizedStrict
                                id="user-home-room-finder-resource-book-button"
                                vars={{ amount: selectedIds.length }}
                            >
                                <Button
                                    onClick={() => setConfirmBookingDialog(true)}
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                >
                                    Book
                                </Button>
                            </LocalizedStrict>
                        </Box>
                    ) : null}
                </Stack>
            </Grid>

            {confirmBookingDialog && bookingConfig && (
                <CreateUpdateBookingDialogWrap
                    open={confirmBookingDialog}
                    onClose={(formValues) => {
                        setSelectedIds(formValues?.meetingDetails.resourceIds.map((i) => +i) ?? [])
                        setConfirmBookingDialog(false)
                    }}
                    event={{
                        id: "",
                        resourceIds: selectedIds.map((id) => id.toString()),
                        start: selectedStartTime!,
                        end: selectedEndTime!,
                    }}
                    initialValues={null}
                    resourcesList={
                        data
                            ?.filter((i) => selectedIds.includes(i.resource.id))
                            .map((resource) => ({
                                id: resource.resource.id,
                                title: resource.resource.name,
                                // hasMessagingEnabled: false,
                                type: {
                                    id: resource.resource.resourceTypeId,
                                    name: resource.resource.name,
                                },
                            })) ?? []
                    }
                    handleSubmit={onCreateBooking}
                    handleClearResourceIds={() => setSelectedIds([])}
                    user={{ userId, displayName: username || "", companyId, userType }}
                    bookingConfig={bookingConfig}
                />
            )}
        </Box>
    )
}
